import {
  useSandbox,
  useStartSandbox,
  useStopSandbox,
  useDestroySandbox,
  ApiError,
  type SandboxStatus,
} from "@vorloop/api-client-react";
import { useState } from "react";

function startErrorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const p = err.problem as { code?: string; detail?: string; title?: string };
    return p.detail ?? p.title ?? "Failed to start sandbox";
  }
  return (err as Error).message ?? "Failed to start sandbox";
}

/**
 * Compact TopBar control for the per-project Fly Machines sandbox.
 *
 * States:
 *   - no sandbox yet → "Start sandbox" button
 *   - starting/running → status pill, click reveals stop/destroy menu
 *   - stopped → "Resume sandbox" button (re-start = create new machine)
 *   - error → status pill with the error tooltip
 *
 * Backed by the chunk-1 routes: GET/POST start/POST stop/DELETE under
 * /api/projects/:id/sandbox. The TanStack Query refetch interval keeps
 * the status badge fresh as Fly transitions the machine.
 */

const STATUS_COLOR: Record<SandboxStatus, string> = {
  starting: "var(--text-secondary)",
  running: "#22c55e",
  stopped: "var(--text-tertiary)",
  destroying: "var(--text-tertiary)",
  destroyed: "var(--text-tertiary)",
  error: "var(--accent-red)",
};

const STATUS_LABEL: Record<SandboxStatus, string> = {
  starting: "Starting…",
  running: "Running",
  stopped: "Stopped",
  destroying: "Destroying…",
  destroyed: "Destroyed",
  error: "Error",
};

export function SandboxControl({ projectId }: { projectId: string }) {
  const { data, isLoading } = useSandbox(projectId);
  const start = useStartSandbox(projectId);
  const stop = useStopSandbox(projectId);
  const destroy = useDestroySandbox(projectId);
  const [menuOpen, setMenuOpen] = useState(false);

  const sandbox = data?.sandbox;
  const busy = start.isPending || stop.isPending || destroy.isPending;
  const startError = startErrorMessage(start.error);

  // No sandbox yet — show single Start button (and an error pill if the
  // last start attempt was blocked by tier / quota / billing).
  if (!isLoading && !sandbox) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={() => start.mutate()}
          disabled={busy}
          title="Start a per-project sandbox on Fly Machines"
          data-testid="sandbox-start-button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 28,
            padding: "0 10px",
            background: "var(--bg-panel-2)",
            border: "1px solid var(--border-default)",
            borderRadius: 7,
            color: busy ? "var(--text-tertiary)" : "var(--text-secondary)",
            fontSize: 11.5,
            cursor: busy ? "wait" : "pointer",
          }}
        >
          <Dot color={STATUS_COLOR.stopped} />
          {busy ? "Starting…" : "Start sandbox"}
        </button>
        {startError && (
          <span
            role="alert"
            title={startError}
            data-testid="sandbox-start-error"
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 28,
              padding: "0 10px",
              maxWidth: 320,
              background: "rgba(248,113,113,0.12)",
              border: "1px solid rgba(248,113,113,0.35)",
              borderRadius: 7,
              color: "var(--accent-red)",
              fontSize: 11,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {startError}
          </span>
        )}
      </div>
    );
  }

  if (!sandbox) {
    // Loading initial state.
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 28,
          padding: "0 10px",
          fontSize: 11.5,
          color: "var(--text-tertiary)",
        }}
      >
        <Dot color="var(--text-tertiary)" />
        Sandbox…
      </div>
    );
  }

  const color = STATUS_COLOR[sandbox.status];
  const label = STATUS_LABEL[sandbox.status];

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="sandbox-status-button"
        title={sandbox.errorMessage ?? `Sandbox on Fly · region ${sandbox.region}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 28,
          padding: "0 10px",
          background: "var(--bg-panel-2)",
          border: "1px solid var(--border-default)",
          borderRadius: 7,
          color: "var(--text-secondary)",
          fontSize: 11.5,
          cursor: "pointer",
        }}
      >
        <Dot color={color} pulse={sandbox.status === "starting"} />
        {label}
      </button>
      {menuOpen && (
        <div
          role="menu"
          onMouseLeave={() => setMenuOpen(false)}
          style={{
            position: "absolute",
            top: 30,
            right: 0,
            minWidth: 180,
            padding: 4,
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            boxShadow: "0 12px 30px -10px rgba(0,0,0,0.5)",
            zIndex: 20,
          }}
        >
          <div
            style={{
              padding: "6px 10px 4px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--text-tertiary)",
            }}
          >
            Sandbox · {sandbox.region}
          </div>
          {sandbox.status === "running" && (
            <MenuItem
              label="Stop"
              testId="sandbox-stop"
              disabled={busy}
              onClick={() => {
                stop.mutate();
                setMenuOpen(false);
              }}
            />
          )}
          {sandbox.status === "stopped" && (
            <MenuItem
              label="Resume (start fresh)"
              testId="sandbox-resume"
              disabled={busy}
              onClick={() => {
                start.mutate();
                setMenuOpen(false);
              }}
            />
          )}
          <MenuItem
            label="Destroy"
            testId="sandbox-destroy"
            disabled={busy || sandbox.status === "destroying" || sandbox.status === "destroyed"}
            danger
            onClick={() => {
              if (!confirm("Destroy this sandbox? Project files are safe in R2.")) return;
              destroy.mutate();
              setMenuOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  disabled,
  danger,
  testId,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "6px 10px",
        background: "transparent",
        border: "none",
        borderRadius: 6,
        color: danger ? "var(--accent-red)" : "var(--text-primary)",
        fontSize: 12.5,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = "var(--bg-panel-2)";
      }}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {label}
    </button>
  );
}

function Dot({ color, pulse }: { color: string; pulse?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: color,
        display: "inline-block",
        ...(pulse && { animation: "vp-pulse 1.2s ease-in-out infinite" }),
      }}
    />
  );
}
